/** Shared helpers for supplier–product link forms (MOQ summary, currency display). */

import type { Product } from '@domain/product/models/Product';
import type { ProductFilter } from '@domain/product/models/ProductFilter';

/** Client-side filter for products already loaded in the link-supplier sheet (mirrors key API filters). */
export function filterProductsForSupplierLink(products: Product[], filter: ProductFilter | undefined): Product[] {
  if (!filter) return products;

  return products.filter(p => {
    const search = filter.search?.trim().toLowerCase();
    if (search) {
      const hay = [p.title, p.ean, p.sku].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(search)) return false;
    }

    if (filter.collectionIds?.length) {
      const ids = new Set(p.collections?.map(c => c.id) ?? []);
      if (!filter.collectionIds.some(id => ids.has(id))) return false;
    }

    if (filter.modes?.length) {
      if (!p.mode || !filter.modes.includes(String(p.mode))) return false;
    }

    if (filter.forecastGrades?.length) {
      if (!p.forecastGrade || !filter.forecastGrades.includes(p.forecastGrade)) return false;
    }

    if (filter.supplierIds?.length) {
      const ids = new Set(p.supplierLinks?.map(s => s.supplierId) ?? []);
      if (!filter.supplierIds.some(id => ids.has(id))) return false;
    }

    if (filter.hasImage === true && !p.image) return false;
    if (filter.hasImage === false && !!p.image) return false;

    if (filter.purchasable === false && p.purchasable) return false;

    if (filter.marketplaceIds?.length) {
      const listingIds = p.listings?.map(l => l.marketplace?.id).filter(Boolean) ?? [];
      if (!filter.marketplaceIds.some(mid => listingIds.includes(mid))) return false;
    }

    if (filter.noMarketplaceListing) {
      if (p.listings && p.listings.length > 0) return false;
    }

    return true;
  });
}

export const currencySymbol = (code: string) => {
  switch (code) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    case 'CHF':
      return 'Fr.';
    default:
      return code;
  }
};

/**
 * MOQ when type is "items": non-negative integers only.
 * Typing is digit-only; if a decimal separator appears (incl. paste), the value is floored.
 */
export function sanitizeMoqItemsInput(raw: string): string {
  if (raw === '') return '';
  const t = raw.trim();
  if (t.includes('.') || t.includes(',')) {
    const n = parseFloat(t.replace(',', '.'));
    if (Number.isFinite(n)) return String(Math.max(0, Math.floor(Math.abs(n))));
    return '';
  }
  return t.replace(/\D/g, '');
}

/** When toggling MOQ type to "items", coerce an existing value (e.g. from currency) to an integer string. */
export function coerceMoqToItemsOnTypeSwitch(raw: string): string {
  const t = raw.trim();
  if (t === '') return '';
  const n = parseFloat(t.replace(',', '.'));
  if (Number.isFinite(n)) return String(Math.max(0, Math.floor(Math.abs(n))));
  return sanitizeMoqItemsInput(t);
}

export const summarizeMoq = (
  row: {
    minimumOrderQuantity: string;
    orderInMultiplesOf: string;
    minimumOrderQuantityType: string;
  },
  currencyCode: string
) => {
  const parts: string[] = [];
  const qty = row.minimumOrderQuantity.trim();
  if (qty) {
    const t = row.minimumOrderQuantityType || 'items';
    parts.push(t === 'currency' ? `${currencySymbol(currencyCode)}${qty}` : `${qty} items`);
  }
  const mult = row.orderInMultiplesOf.trim();
  if (mult) parts.push(`×${mult}`);
  return parts.join(' · ');
};
